import type {
  ComplexSearchSectionsDto,
  WbsClient,
} from '@bldsoft/cappie/api/wbs'
import type { API } from '@setplex/tria-api'
import type { ApiGlobalSearchPayload } from './index.h'
import type { SearchSectionItemDto } from './sections.format'

import { payload } from '@bldsoft/cappie/rest'
import * as v from 'valibot'
import { formatLiveEvent } from '../live-events/live-events.format'
import { formatChannel } from '../tv/tv.format'
import { formatShow } from '../tvshows/tvshows.format'
import { formatVod } from '../vods/vods.format'
import { SearchHintsSchema } from './schemas/hyrid-search'
import { SearchAutocompleteSchema } from './schemas/search-autocomplete'
import {
  formatSearchSections,
  formatSectionItem,
  REQUESTED_SEARCH_SECTIONS,
} from './sections.format'

export function use(
  client: WbsClient,
  globalSearch: API['globalSearch'],
  _api: API
): void {
  //GET /v3/search
  globalSearch.getSearchFx.use(
    async ({ q, count, contentType, contentTypes, cursor }) => {
      const found = (await payload(
        client.get.api.v3.search({
          query: {
            q,
            count: count || undefined,
            contentType: contentType || undefined,
            contentTypes: contentTypes ? String(contentTypes) : undefined,
            cursor: cursor || undefined,
          },
        })
      )) as ApiGlobalSearchPayload

      const { liveEvents, tvChannels, tvShows, vods, radioChannels } = found

      const formattedEvents = (liveEvents?.content || []).map(formatLiveEvent)
      const formattedTvChannels = (tvChannels?.content || []).map(formatChannel)
      const formattedTvShows = (tvShows?.content || []).map(formatShow)
      const formattedVods = (vods?.content || []).map(formatVod)

      return {
        liveEvents: {
          ...liveEvents,
          content: formattedEvents,
        },
        tvChannels: {
          ...tvChannels,
          content: formattedTvChannels,
        },
        tvShows: {
          ...tvShows,
          content: formattedTvShows,
        },
        vods: {
          ...vods,
          content: formattedVods,
        },
        radioChannels: {
          ...radioChannels,
          content: [],
        },
      }
    }
  )

  //GET /v3/search/hybrid, or /v3/search/hybrid/sections when the MW supports it
  globalSearch.getHybridSearchFx.use(async ({ q, sectioned, signal }) => {
    if (sectioned) {
      const sections = (await payload(
        client.get.api.v3.search.hybrid.sections({
          query: { q, sections: REQUESTED_SEARCH_SECTIONS },
          signal,
        })
      )) as ComplexSearchSectionsDto

      return formatSearchSections(sections?.sections ?? [])
    }

    const found = (await payload(
      client.get.api.v3.search.hybrid({ query: { q }, signal })
    )) as SearchSectionItemDto[]

    return found.map(formatSectionItem).filter((item) => item != null)
  })

  //GET /v3/search/hints
  globalSearch.getSearchHintsFx.use(async () => {
    const json = await payload(client.get.api.v3.search.hints())
    return v.parse(SearchHintsSchema, json)
  })

  // GET /v3/search/autocomplete
  globalSearch.getSearchAutocompleteFx.use(async ({ q, signal }) => {
    const json = await payload(
      client.get.api.v3.search.autocomplete({ query: { q }, signal })
    )
    return v.parse(SearchAutocompleteSchema, json) ?? []
  })
}
